import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './GerarBoletoFinanceiro.css';
import { toast } from 'react-toastify';
import SeloAsaas from './SeloAsaas';
import {
  FaFileInvoiceDollar,
  FaUserTie,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaAlignLeft,
  FaCheckCircle,
  FaArrowRight,
  FaPercent,
  FaHandHoldingUsd,
  FaEye,
  FaTimes,
  FaLock,
  FaBarcode,
  FaQrcode,
  FaRegCreditCard,
  FaShieldAlt,
} from 'react-icons/fa';

/* =========================
   Helpers
   ========================= */

function brl(value) {
  const n = Number(value || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseCurrency(value) {
  if (!value) return 0;
  return (
    Number(
      String(value)
        .replace(/\./g, '')
        .replace(',', '.')
    ) || 0
  );
}

function parseNumberBR(value) {
  if (!value) return 0;
  return Number(String(value).replace(',', '.')) || 0;
}

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function todayISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
    .toISOString()
    .slice(0, 10);
}

/* Máscara de moeda reutilizável (retorna string BR "1.234,56") */
function maskCurrency(raw) {
  let value = String(raw).replace(/\D/g, '');
  if (!value) return '';
  value = (value / 100).toFixed(2) + '';
  value = value.replace('.', ',');
  value = value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  return value;
}

/* =========================
   Component
   ========================= */

export default function GerarBoletoFinanceiro({
  goToContasReceberPath = '/financeiro/contas-a-receber',
  nomePlataforma = 'BLB System',
}) {
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const [form, setForm] = useState({
    cliente_id: '',
    descricao: '',
    valor: '',
    data_vencimento: '',
  });

  // ── Split / Comissão da plataforma ──────────────────────────────
  const [comissao, setComissao] = useState({
    ativa: false,
    tipo: 'percentual', // 'percentual' | 'fixo'
    valor: '',
  });

  /* =========================
     Derived state
     ========================= */

  const valorNumerico = useMemo(() => parseCurrency(form.valor), [form.valor]);

  const comissaoCalculada = useMemo(() => {
    if (!comissao.ativa || valorNumerico <= 0) return 0;
    if (comissao.tipo === 'percentual') {
      const pct = clamp(parseNumberBR(comissao.valor), 0, 100);
      return (valorNumerico * pct) / 100;
    }
    return Math.min(parseCurrency(comissao.valor), valorNumerico);
  }, [comissao, valorNumerico]);

  const valorLiquido = useMemo(
    () => Math.max(valorNumerico - comissaoCalculada, 0),
    [valorNumerico, comissaoCalculada]
  );

  const isValid = useMemo(() => {
    return (
      form.cliente_id &&
      form.descricao.trim().length >= 4 &&
      valorNumerico > 0 &&
      form.data_vencimento
    );
  }, [form, valorNumerico]);

  const resumo = useMemo(() => {
    const cliente = clientes.find(
      (c) => String(c.id) === String(form.cliente_id)
    );
    return {
      cliente: cliente?.nome || '—',
      vencimento: form.data_vencimento
        ? new Date(form.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')
        : '—',
      valor: brl(valorNumerico),
      descricao: form.descricao || '—',
    };
  }, [clientes, form, valorNumerico]);

  /* =========================
     Load clientes (API REAL)
     Tenant resolvido no backend via JWT/x-tenant-id (api service).
     ========================= */

  const loadClientes = async () => {
    setLoadingClientes(true);
    try {
      const { data } = await api.get('/api/clientes/select');
      setClientes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erro ao carregar clientes.');
      setClientes([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  useEffect(() => {
    loadClientes();
  }, []);

  /* =========================
     Handlers
     ========================= */

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleValorChange = (e) => setField('valor', maskCurrency(e.target.value));

  const handleComissaoValor = (e) => {
    if (comissao.tipo === 'percentual') {
      // Aceita até 2 casas; trava em 0–100
      let v = e.target.value.replace(/[^\d,]/g, '');
      const num = parseNumberBR(v);
      if (num > 100) v = '100';
      setComissao((prev) => ({ ...prev, valor: v }));
    } else {
      setComissao((prev) => ({ ...prev, valor: maskCurrency(e.target.value) }));
    }
  };

  const toggleComissaoTipo = (tipo) =>
    setComissao((prev) => ({ ...prev, tipo, valor: '' }));

  const resetForm = () => {
    setForm({ cliente_id: '', descricao: '', valor: '', data_vencimento: '' });
    setComissao({ ativa: false, tipo: 'percentual', valor: '' });
  };

  // Monta a observação de conformidade do split — persistida em `observacoes`
  // (a rota manual já grava esse campo; nenhuma mudança de backend necessária).
  const montarObservacaoSplit = () => {
    if (!comissao.ativa || comissaoCalculada <= 0) return null;
    const detalhe =
      comissao.tipo === 'percentual'
        ? `${parseNumberBR(comissao.valor)}%`
        : 'valor fixo';
    return (
      `Comissão da plataforma: ${brl(comissaoCalculada)} (${detalhe}). ` +
      `Valor líquido ao recebedor: ${brl(valorLiquido)}. ` +
      `A comissão é cobrada pela plataforma e não constitui tarifa pelos ` +
      `serviços financeiros, que são processados pelo Asaas.`
    );
  };

  // ENVIO PARA A API REAL
  const submit = async () => {
    if (!isValid) {
      toast.warn('Preencha todos os campos obrigatórios.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/contas-a-receber/manual', {
        cliente_id: form.cliente_id,
        descricao: form.descricao.trim(),
        valor: valorNumerico,
        data_vencimento: form.data_vencimento,
        observacoes: montarObservacaoSplit(), // null quando não há split
      });

      toast.success('Título gerado com sucesso e lançado em Contas a Receber.');

      localStorage.setItem('financeiro:contas_receber_refresh', String(Date.now()));
      window.location.href = goToContasReceberPath;
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao gerar título de cobrança.');
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
     Render
     ========================= */

  return (
    <div className="boleto-page">
      {/* HEADER */}
      <div className="boleto-header">
        <div className="boleto-title">
          <div className="icon-wrapper">
            <FaFileInvoiceDollar className="boleto-icon" />
          </div>
          <div>
            <h1>Gerar Título de Cobrança</h1>
            <p>Criação manual de contas a receber</p>
          </div>
        </div>

        {/* Selo no topo do fluxo financeiro (conformidade sempre visível) */}
        <SeloAsaas largura={150} altura={44} />
      </div>

      {/* GRID */}
      <div className="boleto-grid">
        {/* FORM */}
        <section className="card form-card">
          <div className="card-head">
            <h2>Dados do Título</h2>
            <div className="status-inline">
              Status inicial:
              <span className="chip status-pendente">PENDENTE</span>
            </div>
          </div>

          <div className="form">
            <div className="field">
              <label>
                <FaUserTie /> Cliente
              </label>
              <select
                value={form.cliente_id}
                onChange={(e) => setField('cliente_id', e.target.value)}
                disabled={loadingClientes}
              >
                <option value="">
                  {loadingClientes ? 'Carregando…' : 'Selecione o cliente'}
                </option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>
                <FaCalendarAlt /> Vencimento
              </label>
              <input
                type="date"
                min={todayISO()}
                value={form.data_vencimento}
                onChange={(e) => setField('data_vencimento', e.target.value)}
              />
            </div>

            <div className="field span-2">
              <label>
                <FaAlignLeft /> Descrição
              </label>
              <input
                value={form.descricao}
                onChange={(e) => setField('descricao', e.target.value)}
                placeholder="Ex: Mensalidade, serviço de remoção, etc."
              />
            </div>

            <div className="field">
              <label>
                <FaMoneyBillWave /> Valor (R$)
              </label>
              <input
                value={form.valor}
                onChange={handleValorChange}
                placeholder="0,00"
                inputMode="numeric"
              />
            </div>

            {/* ── SPLIT / COMISSÃO DA PLATAFORMA ───────────────────── */}
            <div className="field span-2 split-block">
              <div className="split-head">
                <label className="split-title">
                  <FaHandHoldingUsd /> Comissão da plataforma (Split)
                </label>

                <button
                  type="button"
                  role="switch"
                  aria-checked={comissao.ativa}
                  className={`switch ${comissao.ativa ? 'switch--on' : ''}`}
                  onClick={() =>
                    setComissao((prev) => ({ ...prev, ativa: !prev.ativa }))
                  }
                >
                  <span className="switch__thumb" />
                </button>
              </div>

              {comissao.ativa && (
                <div className="split-body">
                  <div className="split-controls">
                    <div className="segmented">
                      <button
                        type="button"
                        className={comissao.tipo === 'percentual' ? 'seg seg--on' : 'seg'}
                        onClick={() => toggleComissaoTipo('percentual')}
                      >
                        <FaPercent /> Percentual
                      </button>
                      <button
                        type="button"
                        className={comissao.tipo === 'fixo' ? 'seg seg--on' : 'seg'}
                        onClick={() => toggleComissaoTipo('fixo')}
                      >
                        R$ Fixo
                      </button>
                    </div>

                    <div className="split-input-wrap">
                      <span className="split-adorn">
                        {comissao.tipo === 'percentual' ? '%' : 'R$'}
                      </span>
                      <input
                        className="split-input"
                        value={comissao.valor}
                        onChange={handleComissaoValor}
                        placeholder={comissao.tipo === 'percentual' ? '0' : '0,00'}
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {/* Quebra transparente do split */}
                  <div className="split-breakdown">
                    <div className="sb-row">
                      <span>Valor cobrado do cliente</span>
                      <strong>{brl(valorNumerico)}</strong>
                    </div>
                    <div className="sb-row sb-row--minus">
                      <span>Comissão da plataforma</span>
                      <strong>− {brl(comissaoCalculada)}</strong>
                    </div>
                    <div className="sb-row sb-row--net">
                      <span>Repassado ao recebedor</span>
                      <strong>{brl(valorLiquido)}</strong>
                    </div>
                  </div>

                  {/* Disclaimer regulatório (Res. Conjunta 16 e 17 BACEN) */}
                  <p className="split-disclaimer">
                    <FaShieldAlt />
                    <span>
                      Esta comissão é cobrada por <b>{nomePlataforma}</b> e não é
                      uma tarifa pela forma de pagamento. Os serviços financeiros
                      são processados pelo <b>Asaas</b>, que não cobra por esta operação.
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="field actions span-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={resetForm}
                disabled={submitting}
              >
                Limpar
              </button>

              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowCheckout(true)}
                disabled={!isValid || submitting}
                title={!isValid ? 'Preencha os dados para pré-visualizar' : ''}
              >
                <FaEye />
                Pré-visualizar checkout
              </button>

              <button
                type="button"
                className="btn-primary"
                onClick={submit}
                disabled={!isValid || submitting}
              >
                <FaCheckCircle />
                {submitting ? 'Gerando…' : 'Gerar Título'}
                <FaArrowRight />
              </button>
            </div>
          </div>
        </section>

        {/* PREVIEW */}
        <aside className="card preview-card">
          <div className="card-head">
            <h2>Resumo da Operação</h2>
            <span className={`chip ${isValid ? 'pill-ok' : 'pill-warn'}`}>
              {isValid ? 'Pronto para gerar' : 'Incompleto'}
            </span>
          </div>

          <div className="preview">
            <div className="row">
              <span className="k">Cliente</span>
              <span className="v">{resumo.cliente}</span>
            </div>
            <div className="row">
              <span className="k">Vencimento</span>
              <span className="v">{resumo.vencimento}</span>
            </div>
            <div className="row">
              <span className="k">Descrição</span>
              <span className="v">{resumo.descricao}</span>
            </div>

            {comissao.ativa && comissaoCalculada > 0 && (
              <>
                <div className="row">
                  <span className="k">Comissão da plataforma</span>
                  <span className="v">− {brl(comissaoCalculada)}</span>
                </div>
                <div className="row">
                  <span className="k">Repasse ao recebedor</span>
                  <span className="v">{brl(valorLiquido)}</span>
                </div>
              </>
            )}

            <div className="row highlight-row">
              <span className="k">Valor Total</span>
              <span className="v big">{resumo.valor}</span>
            </div>
          </div>

          <div className="note">
            <p>
              <b>Fluxo financeiro:</b>
            </p>
            <ul>
              <li>
                O título nascerá como <b>PENDENTE</b> no contas a receber.
              </li>
              <li>A entrada no caixa/banco ocorrerá somente na baixa do pagamento.</li>
            </ul>
          </div>

          {/* Selo no rodapé do resumo (documento financeiro) */}
          <div className="selo-footer">
            <SeloAsaas largura={140} altura={42} legenda />
          </div>
        </aside>
      </div>

      {/* ── MODAL: PRÉ-VISUALIZAÇÃO DO CHECKOUT DO CLIENTE FINAL ──────── */}
      {showCheckout && (
        <CheckoutPreview
          onClose={() => setShowCheckout(false)}
          descricao={form.descricao || 'Título de cobrança'}
          valor={valorNumerico}
          comissaoAtiva={comissao.ativa && comissaoCalculada > 0}
          nomePlataforma={nomePlataforma}
        />
      )}
    </div>
  );
}

/* =========================================================================
   CheckoutPreview — espelho fiel da tela que o cliente final verá.
   Serve para validar a "adequação das interfaces" (selo + transparência)
   ANTES da análise da Asaas. Somente leitura.
   ========================================================================= */

function CheckoutPreview({
  onClose,
  descricao,
  valor,
  comissaoAtiva,
  nomePlataforma,
}) {
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const brlValor = brl(valor);

  return (
    <div
      className="checkout-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Pré-visualização do checkout"
    >
      <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-modal__bar">
          <span className="checkout-modal__badge">
            <FaEye /> Como o cliente vê
          </span>
          <button className="checkout-modal__close" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        </div>

        {/* Simulação do navegador do cliente */}
        <div className="ck">
          <div className="ck__topbar">
            <span className="ck__dot" />
            <span className="ck__url">
              <FaLock /> {nomePlataforma.toLowerCase().replace(/\s+/g, '')}.com.br/pagamento
            </span>
          </div>

          <div className="ck__body">
            <h3 className="ck__h">Finalizar pagamento</h3>

            <div className="ck__accordion">
              <span>Identificação</span>
              <span className="ck__done">Completo</span>
            </div>
            <div className="ck__accordion">
              <span>Endereço</span>
              <span className="ck__done">Completo</span>
            </div>

            <div className="ck__pay">
              <p className="ck__pay-title">Pagamento</p>
              <div className="ck__tabs">
                <button className="ck__tab ck__tab--on">
                  <FaQrcode /> Pix
                </button>
                <button className="ck__tab">
                  <FaRegCreditCard /> Cartão
                </button>
                <button className="ck__tab">
                  <FaBarcode /> Boleto
                </button>
              </div>

              <div className="ck__qr">
                <div className="ck__qr-fake" aria-hidden="true">
                  <FaQrcode />
                </div>
                <p>Escaneie o QR Code no app do seu banco</p>
              </div>
            </div>

            <div className="ck__summary">
              <p className="ck__sum-title">Resumo do pagamento</p>
              <div className="ck__sum-row">
                <span>{descricao}</span>
                <span>{brlValor}</span>
              </div>
              {comissaoAtiva && (
                <div className="ck__sum-note">
                  Comissão de {nomePlataforma} inclusa — não é tarifa dos
                  serviços financeiros.
                </div>
              )}
              <div className="ck__sum-total">
                <span>Total</span>
                <strong>{brlValor}</strong>
              </div>
            </div>

            <button className="ck__cta">Pagar {brlValor}</button>

            {/* Selo + site seguro — exatamente como o playbook exige */}
            <div className="ck__footer">
              <SeloAsaas largura={132} altura={40} />
              <span className="ck__secure">
                <FaShieldAlt /> Site seguro
              </span>
            </div>
          </div>
        </div>

        <p className="checkout-modal__hint">
          Esta é uma pré-visualização interna. Use-a para conferir a aplicação
          do selo e a transparência do split antes de enviar o formulário à Asaas.
        </p>
      </div>
    </div>
  );
}